import { useState } from 'react';
import { Button, ButtonGroup, InlineStack, Paragraph, Stack, TextArea } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { EditTrigger } from './edit-trigger';
import { RowClickGuard } from './row-click-guard';
import { descriptionCounter, descriptionError } from '../utils/editing';
import type { DataIndexAnalyticsType } from '../utils/types';
import { GenerateDescriptionButton } from './generate-description-button';

/**
 * A row's description, written in place.
 *
 * The description is what the next person reading this list learns about the
 * row, so it is editable from the list rather than only from the row's own
 * page. A row nobody may describe shows a dash: an invitation the page would
 * refuse is worse than none.
 */

const NO_PERMISSION_TOOLTIP = 'Only workspace admins can edit attribute descriptions.';

export type DescriptionEditorProps = {
  workspaceId: number;
  /** What the row is called, which is also what the model is asked about. */
  name: string;
  type: DataIndexAnalyticsType;
  objectTypeId?: number;
  description: string | null;
  /** False for a reserved name, or for a viewer without the permission. */
  canEdit: boolean;
  /** Whether the viewer would be allowed if the row were not reserved. */
  hasPermission: boolean;
  /** Resolves to whether the save went through; a failure keeps the box open. */
  onSave: (description: string) => Promise<boolean>;
};

export function DescriptionEditor({
  workspaceId,
  name,
  type,
  objectTypeId,
  description,
  canEdit,
  hasPermission,
  onSave,
}: DescriptionEditorProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  if (draft == null) {
    return (
      <RowClickGuard>
        <DescriptionTrigger
          description={description}
          canEdit={canEdit}
          hasPermission={hasPermission}
          onStart={() => setDraft(description ?? '')}
        />
      </RowClickGuard>
    );
  }

  const error = descriptionError(draft);

  const save = async () => {
    if (error != null || isSaving) {
      return;
    }
    setIsSaving(true);
    const saved = await onSave(draft);
    setIsSaving(false);
    if (saved) {
      setDraft(null);
    }
  };

  return (
    <RowClickGuard>
      <InlineStack gap="100">
        <Stack gap="050" flexGrow="1">
          <TextArea
            ariaLabel={`Description for ${name}`}
            autoFocus={true}
            rows={3}
            value={draft}
            placeholder="Description…"
            isInvalid={error != null}
            error={error}
            isDisabled={isSaving}
            onInput={(event) => setDraft(event.target.value)}
          />
          {error == null ? (
            <Paragraph size="xs" variant={isEmptyString(draft) ? 'secondary' : undefined}>
              {descriptionCounter(draft)}
            </Paragraph>
          ) : null}
          <GenerateDescriptionButton
            workspaceId={workspaceId}
            type={type}
            name={name}
            objectTypeId={objectTypeId}
            isDisabled={error != null || isSaving}
            // Guarded: a generation that lands after Cancel must not reopen
            // the box with text nobody asked to keep.
            onGenerated={(generated) => setDraft((current) => (current == null ? null : generated))}
          />
        </Stack>
        <ButtonGroup groupVariant="spaced">
          <Button
            variant="primary"
            size="sm"
            icon="check"
            isIconOnly
            aria-label="Save description"
            isDisabled={error != null}
            isLoading={isSaving}
            onClick={() => void save()}
          />
          <Button
            variant="secondary"
            size="sm"
            icon="close"
            isIconOnly
            aria-label="Cancel editing"
            isDisabled={isSaving}
            onClick={() => setDraft(null)}
          />
        </ButtonGroup>
      </InlineStack>
    </RowClickGuard>
  );
}

type DescriptionTriggerProps = {
  description: string | null;
  canEdit: boolean;
  hasPermission: boolean;
  onStart: () => void;
};

/** The description as it reads when nobody is editing it. */
function DescriptionTrigger({ description, canEdit, hasPermission, onStart }: DescriptionTriggerProps) {
  return (
    <EditTrigger canEdit={canEdit} hasPermission={hasPermission} tooltip={NO_PERMISSION_TOOLTIP} onStart={onStart}>
      {isEmptyString(description) ? (canEdit ? 'Add description' : '-') : description}
    </EditTrigger>
  );
}
